/**
 * ============================================================
 * client.ts — Cliente Supabase Singleton
 * ============================================================
 *
 * Responsabilidade: Criar e exportar a instancia unica do
 *   cliente Supabase usada em toda a aplicacao. Configurado
 *   com persistencia de sessao, auto-refresh de token e
 *   schema configuravel via env var.
 *
 * Configuracao de Auth:
 *   - persistSession: true (sessao sobrevive reload)
 *   - autoRefreshToken: true (renova JWT antes de expirar)
 *   - flowType: 'implicit' (OAuth redirect flow)
 *   - lock desabilitado para evitar "Lock broken by steal"
 *     em React Strict Mode (double mount em dev)
 *
 * Variaveis de ambiente obrigatorias:
 *   - VITE_SUPABASE_URL
 *   - VITE_SUPABASE_PUBLISHABLE_KEY
 *
 * @example
 *   import { supabase } from '@/integrations/supabase/client';
 * ============================================================
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SUPABASE_SCHEMA = import.meta.env.VITE_SUPABASE_SCHEMA || 'public';

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'FATAL: Supabase environment variables are missing. ' +
    'Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in your .env file.'
  );
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    flowType: 'implicit',
    storageKey: 'sb-legacy-app-auth',
    // Disable navigator.locks to prevent "Lock broken by steal" errors in React Strict Mode
    lock: (name: string, acquireTimeout: number, fn: () => Promise<any>) => fn(),
  },
  db: {
    schema: SUPABASE_SCHEMA as 'public',
  },
});
